function isDeliveryDateValid(dateString, disabledDates) {
  const selectedDate = new Date(dateString);
  // Use the UTC day to avoid timezone issues.
  const dayOfWeek = selectedDate.getUTCDay();

  if (disabledDates.includes(dateString)) {
    return false;
  }

  // 4 = Thursday, 5 = Friday
  return dayOfWeek === 4 || dayOfWeek === 5;
}

/**
 * Returns the London-local calendar parts for a given Date, accounting for GMT/BST automatically.
 * Uses the Intl.DateTimeFormat API — no third-party library required.
 *
 * @param {Date} date
 * @returns {{ year: number, month: number, day: number, hour: number }}
 */
function getLondonDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = {};
  parts.forEach(({ type, value }) => {
    values[type] = value;
  });

  return {
    year: parseInt(values.year, 10),
    month: parseInt(values.month, 10),
    day: parseInt(values.day, 10),
    hour: parseInt(values.hour, 10),
  };
}

function calculateMinDate(today) {
  // Use the London calendar date as well as the London hour. Around midnight in
  // BST, the UTC date is still the previous day and would apply the wrong cutoff.
  const londonDate = getLondonDateParts(today);
  const minDate = new Date(Date.UTC(londonDate.year, londonDate.month - 1, londonDate.day));

  // If the order is placed at or after 22:00 London time, treat it as the next calendar day
  // for cutoff purposes.
  if (londonDate.hour >= 22) {
    minDate.setUTCDate(minDate.getUTCDate() + 1);
  }

  const currentDay = minDate.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  let daysToAdd;

  switch (currentDay) {
    case 2: // Tuesday  — cutoff day for Thursday delivery
      // Next available is Thursday of the same week
      daysToAdd = 2;
      break;
    case 3: // Wednesday — cutoff day for Friday delivery
      // Next available is Friday of the same week
      daysToAdd = 2;
      break;
    case 4: // Thursday — Friday's Wednesday cutoff has passed
      // Next available is Thursday of the following week
      daysToAdd = 7;
      break;
    default:
      // For Fri(5), Sat(6), Sun(0), Mon(1)
      // Next available is the upcoming Thursday
      daysToAdd = (4 - currentDay + 7) % 7;
  }

  minDate.setUTCDate(minDate.getUTCDate() + daysToAdd);

  return minDate;
}

/**
 * Custom element that wires up the delivery-date input. Using a custom element
 * means `connectedCallback` re-runs every time a fresh instance is connected to
 * the DOM — including after Shopify's Section Rendering API replaces the cart
 * footer / product info container. (Re-injected `<script>` tags do NOT execute,
 * which is why the previous inline-script approach silently stopped working
 * after a variant change or cart update.)
 *
 * Expected markup:
 *   <delivery-date-input data-disabled-dates='["YYYY-MM-DD", ...]'>
 *     <input id="deliveryDateInput" type="date" required ...>
 *   </delivery-date-input>
 */
// Guard against double-registration if the script tag somehow runs twice.
if (typeof customElements !== 'undefined' && !customElements.get('delivery-date-input')) {
  class DeliveryDateInput extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      const input = this.querySelector('input[type="date"]');
      if (!input) return;
      this.input = input;

      let disabledDates = [];
      try {
        disabledDates = JSON.parse(this.dataset.disabledDates || '[]');
      } catch (e) {
        console.warn('delivery-date-input: invalid data-disabled-dates JSON', e);
      }
      this.disabledDates = disabledDates;

      // Cutoff-aware minimum date (Tue/Wed 22:00 London cutoffs).
      const today = new Date();
      const minDate = calculateMinDate(today);
      input.min = minDate.toISOString().split('T')[0];

      // Allow ordering up to 4 months ahead.
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 4);
      input.max = maxDate.toISOString().split('T')[0];

      // Re-validate any value the input already has (e.g. preserved from cart
      // attributes) against the freshly-calculated min/disabled-date rules.
      if (input.value) {
        this.validate(input.value);
      }

      input.addEventListener('input', this.onInput, true);
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('input', this.onInput, true);
      }
    }

    onInput(event) {
      const dateString = event.target.value;
      if (!this.validate(dateString)) return;

      // Persist to cart attributes so the value survives page navigation /
      // section re-renders. Errors here shouldn't break the UI.
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { 'Next delivery date': dateString } }),
      }).catch((err) => console.warn('delivery-date-input: cart update failed', err));
    }

    validate(dateString) {
      if (!dateString) {
        // Let the native `required` attribute handle empty values.
        this.input.setCustomValidity('');
        return false;
      }
      // Also enforce the min boundary in JS, because Safari iOS ignores `min`
      // on <input type="date"> for some users.
      if (this.input.min && dateString < this.input.min) {
        this.input.setCustomValidity(
          'This date is too soon — please pick a later Thursday or Friday.'
        );
        return false;
      }
      if (!isDeliveryDateValid(dateString, this.disabledDates)) {
        this.input.setCustomValidity(
          'Please select an available Thursday or Friday for delivery'
        );
        return false;
      }
      this.input.setCustomValidity('');
      return true;
    }
  }

  customElements.define('delivery-date-input', DeliveryDateInput);
}
